// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2021-06-11 13:25:01 +0200 (Fri, 11 Jun 2021) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 120136 $
//  Purpose   : i3f support
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Loader, Menu, Dropdown, Icon, Segment, Rail, Table, Message} from 'semantic-ui-react'
import {JSONTree} from 'uix/chunks'

// ---Framework imports
import {Sel} from 'uix/registries'
import {XMenu, HeaderSegment} from 'uix/components'
import {get, dict, uniq, strip, size, isArray, isString, flatten} from 'ldash'
import {Part} from 'uix/parts'
import {useState, useSelector, useEffect, usePersistentShallow} from 'uix/hooks'
import log from 'uix/logging'
import {useTypeInfo} from 'uix/types/hooks'

// ---Local imports
import styles from './i3f.scss'

import {useI3F} from './hooks'

const I3FImage = ({
  server,
  id,
  region = 'full',
  size = 1000,
  rotation = 0,
  quality = 'native',
  format = 'jpg',
}) => (
  <img
    src={
      server + '/' + id + '/' + region + '/' + size + '/' + rotation + '/' + quality + '.' + format
    }
  />
)

// VL/I3F-Routing
const MANIFEST = ({id, server = '', version = 'v21'}) => `${server}/i3f/${version}/${id}/manifest`

const SOME_DEFAULTS = [
  'https://visuallibrary.net/i3f/v20/3/manifest',
  'https://ulbhalvd.rc.vls.io/i3f/v20/8848994/manifest',
  'https://api.digitale-sammlungen.de/iiif/presentation/v2/bsb00095783/manifest',
]

export const getMembersFlat = (parent, level, path = []) =>
  flatten(
    (parent.members || []).map(e =>
      e['@type'] == 'sc:Canvas'
        ? {...e, pid: parent['@id'], level, parent, path}
        : getMembersFlat(e, level + 1, [...path, e['@id']])
    )
  )

const Summary = ({i3fdata}) => {
  let lang = useSelector(Sel.language)

  let build = ({label, value}) => {
    let decode_lang = s => {
      if (isArray(s)) {
        let found = s.filter(e => e['@language'] == lang)
        if (found.length > 0) {
          return found[0]['@value']
        } else {
          return s[0]['@value'] || 'no @value'
        }
      }
      return s
    }
    //~ log(label, value)
    let l = decode_lang(label)
    let v = decode_lang(value)

    if (isString(v) && v.includes('<') && v.includes('>')) {
      v = <span dangerouslySetInnerHTML={{__html: v}} />
    }

    return (
      <Table.Row key={l}>
        <Table.Cell style={{width: '90px'}}>{l}</Table.Cell>
        <Table.Cell>{v}</Table.Cell>
      </Table.Row>
    )
  }

  let rowsMetadata = get(i3fdata, 'metadata', []).map(e => build(e))
  let canvases = get(i3fdata, 'sequences[0].canvases', [])
  let structures = get(i3fdata, 'structures', [])
  let toc_count = structures.length

  // v21
  let rangeMembers = get(i3fdata, 'structures[0].members[0]')

  if (rangeMembers) {
    let flat = getMembersFlat(rangeMembers, 0)
    //~ pages = flat.length
    toc_count = uniq(flat.map(e => e.pid)).length
  }

  let calculated = [
    ['TOC', toc_count],
    ['Canvases', canvases.length],
    ['Members', String(Boolean(rangeMembers))],
  ]
  let rowsCalculated = calculated.map(([label, value]) => build({label, value}))

  return (
    <>
      <Table definition compact>
        <Table.Body>{rowsMetadata}</Table.Body>
      </Table>
      <Table definition compact>
        <Table.Body>{rowsCalculated}</Table.Body>
      </Table>
    </>
  )
}

const IMenu = ({manifest, manifests, setManifest, setManifests, options, setOptions}) => {
  let apiurl = useSelector(Sel.apiurl)

  let vOptions = ['v20', 'v21'].map(m => ({key: m, text: m, value: m}))
  let mOptions = manifests.map(m => ({key: m, text: m, value: m}))

  let handleAddition = (e, {value}) => setManifests(uniq([...manifests, strip(value)]))
  let handleChange = (e, {value}) => setManifest(strip(value))

  return (
    <XMenu>
      <Menu.Item
        icon="download"
        title={`open raw ${apiurl.href(manifest)}`}
        onClick={() => window.open(apiurl.href(manifest), 'i3f')}
      />
      <Menu.Item
        icon="external alternate"
        title="open in i3f.vls.io"
        onClick={() =>
          window.open(
            `https://i3f.vls.io/?id=${encodeURIComponent(apiurl.baseUrl(manifest))}`,
            'i3f.vls.io'
          )
        }
      />
      <Menu.Item>
        <Dropdown
          options={vOptions}
          value={options.version}
          onChange={(e, {value}) => setOptions({version: value})}
        />
      </Menu.Item>

      <Dropdown
        options={mOptions}
        placeholder="Choose Manifest"
        search
        selection
        allowAdditions
        value={manifest}
        onAddItem={handleAddition}
        onChange={handleChange}
      />
    </XMenu>
  )
}

const I3FView = ({i18n}) => {
  let [manifest, setManifest] = useState(null)
  let [options, setOptions] = usePersistentShallow('i3fopts', {version: 'v20'})

  let request = useSelector(Sel.request)
  let m = {id: request.id, version: options.version}
  let typeInfo = useTypeInfo(request.id)

  useEffect(() => setManifest(null), [request.id, options])
  if (!manifest) {
    let enabled = get(typeInfo, 'cfg.i3f.on')
    if (enabled) {
      manifest = MANIFEST(m)
    }
  }

  let [manifests, setManifests] = useState(SOME_DEFAULTS)
  manifests = uniq([...manifests, manifest])

  let data = useI3F({manifest})

  let shouldExpandNode = (keyName, data, level) => {
    return level == 1
  }
  let valueRenderer = (raw, value, ...path) => raw

  let menu = <IMenu {...{manifest, setManifest, manifests, setManifests, options, setOptions}} />

  let content = <Loader active />
  if (!manifest) {
    content = <Message color="olive">Inaktiv: {i18n.getType(typeInfo && typeInfo.name)}</Message>
  }

  if (size(data) > 0) {
    content = (
      <Segment tertiary attached>
        <JSONTree
          hideRoot={true}
          data={data}
          shouldExpandNode={shouldExpandNode}
          valueRenderer={valueRenderer}
        />
        <Rail attached internal position="right">
          <Segment tertiary attached>
            {data && <Summary i3fdata={data} />}
          </Segment>
        </Rail>
      </Segment>
    )
  }
  return (
    <HeaderSegment
      major
      header={i18n.get(`I3F`)}
      subheader={manifest}
      icon="list"
      portal="I3FDetailsPortal"
      menu={menu}
      className={styles.i3fdetails}
    >
      {content}
    </HeaderSegment>
  )
}

export class I3FDetails extends Part {
  _render() {
    return <I3FView i18n={this.props.i18n} />
  }
}
